import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { isNil, noop } from 'lodash';
import { AttachFile, ClearSharp } from '@material-ui/icons';

import { Checkbox } from 'syngenta-digital-cropwise-react-ui-kit';
import GroupTable from 'components/UiKit/GroupTable';

import RoundedDisplay from 'components/RoundedDisplay';

import FieldIcon from '../../../../../../../components/Icons/FieldIcon';
import {
  parsePropertyInfoFromFeatureCollection,
  selectedItemAtIndex,
  createRowGroups
} from '../../../../../helpers/fieldDataHelpers';
import { calculateFeaturesTotalAreaInAcres } from '../../../../../helpers/propertyDataHelpers';

// The whole file's structure is listed so the user can find a shape; only the
// level that fits the screen can be ticked.
const mapFieldsToRows = (items, _depth = 0) =>
  !items?.length
    ? []
    : items.flatMap((item, index) => {
        const newRow = {
          ...item,
          $depth: _depth,
          $acres: calculateFeaturesTotalAreaInAcres(item.features),
          parentReference: _depth === 0 ? 0 : index + 1
        };
        if (item.items?.length)
          return [newRow].concat(mapFieldsToRows(item.items, _depth + 1));
        return newRow;
      });

// eslint-disable-next-line react/prop-types
const FieldCard = ({ name, farmName, features, index }) => {
  return (
    <div className="ml-2 flex">
      <FieldIcon
        id={`import-field-icon-${index}`}
        features={features}
        color="#696F88"
      />
      <div className="flex flex-col justify-center ml-2">
        <div className="text-neutral-1000">{name || 'Boundary'}</div>
        {farmName && <div style={{ color: '#707374' }}>{farmName}</div>}
      </div>
    </div>
  );
};

// eslint-disable-next-line react/prop-types
const CropZoneCard = ({ name, features, index }) => {
  return (
    <div className="ml-2 flex">
      <FieldIcon
        id={`import-crop-zone-icon-${index}`}
        features={features}
        color="#CBCDD5"
      />
      <div className="flex flex-col justify-center ml-2 text-neutral-1000">
        {name || 'Boundary'}
      </div>
    </div>
  );
};

const PropertySelector = ({
  geoJson = null,
  fileName,
  onSelect,
  onCancel,
  selectableType = 'field'
}) => {
  const [rowStates, setRowStates] = useState();
  const [rows, setRows] = useState([]);
  const propertyOptionsRef = useRef(
    parsePropertyInfoFromFeatureCollection(geoJson)
  );

  useEffect(() => {
    setRows(mapFieldsToRows(propertyOptionsRef.current));
  }, []);

  useEffect(() => {
    if (!isNil(rowStates)) {
      const selections = rowStates
        .filter(({ checked }) => checked)
        .map(({ index }) => rows[index])
        .filter(({ type }) => type === selectableType)
        .flatMap(({ features }) => features);
      onSelect(selections);
    }
  }, [onSelect, rows, rowStates, selectableType]);

  const handleSelect = (idx, evt) => {
    setRowStates((previousRowStates = []) => {
      if (previousRowStates.find(selectedItemAtIndex(idx))) {
        return previousRowStates.map(({ index, checked, event }) => ({
          index,
          event: index === idx ? evt : event,
          checked: index === idx ? !checked : checked
        }));
      }
      return previousRowStates.concat([
        { index: idx, event: evt, checked: true }
      ]);
    });
  };

  const rowGroups = createRowGroups(rows);

  return (
    <>
      <div
        className="flex items-center justify-between"
        style={{ backgroundColor: '#EAF6FF', color: '#0071CD' }}
      >
        <div>
          <AttachFile
            fontSize="small"
            className="text-neutral-600"
            style={{ height: 16 }}
          />
          {fileName}
        </div>
        <button type="button" onClick={onCancel}>
          <ClearSharp
            fontSize="small"
            className="mx-2 text-neutral-600"
            style={{ height: 12 }}
          />
        </button>
      </div>
      <div className="overflow-auto my-8" style={{ maxHeight: '36rem' }}>
        <GroupTable selectable={false} onSelect={noop}>
          {rowGroups.map(groupRows => (
            <GroupTable.RowGroup
              key={`row-group-${groupRows[0]?.[0]}-${groupRows[0]?.[1]}`}
            >
              {groupRows.map(
                ([
                  parentIndex,
                  childIndex,
                  { name, farmName, type, features, $depth, $acres }
                ]) => {
                  const isSelectable = type === selectableType;
                  return (
                    <GroupTable.Row
                      key={`${type}${name}${parentIndex}${childIndex}`}
                    >
                      <div
                        className={`flex flex-1 justify-between items-center p-2 text-gray-600 focus:outline-hidden ${
                          isSelectable ? 'hover:bg-gray-100' : 'opacity-50'
                        }`}
                        data-testid="land-selector-row-item"
                        aria-disabled={!isSelectable}
                      >
                        <div className="flex flex-1 items-center ml-2">
                          {Array.from({ length: $depth }, (el, index) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <div key={`tab-${index}`} className="mx-2" />
                          ))}
                          <Checkbox
                            checked={
                              isSelectable &&
                              !!rowStates?.find(
                                ({ index, checked }) =>
                                  index === childIndex && checked
                              )
                            }
                            disabled={!isSelectable}
                            onChange={event =>
                              isSelectable && handleSelect(childIndex, event)
                            }
                          />
                          {type === 'field' && (
                            <FieldCard
                              name={name}
                              farmName={farmName}
                              features={features}
                              index={childIndex}
                            />
                          )}
                          {type === 'cropZone' && (
                            <CropZoneCard
                              name={name}
                              features={features}
                              index={childIndex}
                            />
                          )}
                        </div>
                        <div className="flex flex-col justify-center">
                          <RoundedDisplay units="ac.">{$acres}</RoundedDisplay>
                        </div>
                      </div>
                    </GroupTable.Row>
                  );
                }
              )}
            </GroupTable.RowGroup>
          ))}
        </GroupTable>
      </div>
    </>
  );
};

PropertySelector.propTypes = {
  geoJson: PropTypes.shape({
    features: PropTypes.arrayOf(PropTypes.object).isRequired
  }),
  fileName: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  selectableType: PropTypes.oneOf(['field', 'cropZone'])
};

export default PropertySelector;
